import { useCallback, useMemo, useState } from 'react';

import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { CarFilters, CarVehicle } from '../CarRentalConsole.types';
import { CAR_PRICE_RANGE, DEFAULT_CAR_FILTERS } from '../CarRentalConsole.constants';
import { bucketForPassengers, computeDynamicCarPriceMax, getCarRefundability, getVehiclePrice } from '../CarRentalConsole.utils';

const cancellationTypeOf = (vehicle: CarVehicle) => getCarRefundability(vehicle.cancellationPolicies);

export const useCarFilters = (vehicles: ReadonlyArray<CarVehicle>) => {
  const [filters, setFilters] = useState<CarFilters>(DEFAULT_CAR_FILTERS);
  const [isPriceRangeCustomized, setIsPriceRangeCustomized] = useState(false);
  const [carNameSearch, setCarNameSearch] = useState('');

  const { priceByVehicle, prices, carTypeCounts, companyCounts, transmissionCounts, fuelTypeCounts, driveTypeCounts, cancellationTypeCounts, passengerBucketCounts } =
    useMemo(() => {
      const priceList: number[] = [];
      const fuel: Record<string, number> = {};
      const drive: Record<string, number> = {};
      const carType: Record<string, number> = {};
      const company: Record<string, number> = {};
      const passenger: Record<string, number> = {};
      const transmission: Record<string, number> = {};
      const cancellation: Record<string, number> = {};

      const priceMap = new Map<CarVehicle, number>();

      const tally = (counts: Record<string, number>, key: string | undefined | null) => {
        if (key) counts[key] = (counts[key] ?? 0) + 1;
      };

      vehicles.forEach(vehicle => {
        const price = getVehiclePrice(vehicle);
        priceMap.set(vehicle, price);
        priceList.push(price);
        tally(carType, vehicle.vehicle.category);
        tally(company, vehicle.vendor.name);
        tally(transmission, vehicle.vehicle.transmissionType);
        tally(fuel, vehicle.vehicle.fuelType);
        tally(drive, vehicle.vehicle.driveType);
        tally(cancellation, cancellationTypeOf(vehicle));
        const bucketKey = String(bucketForPassengers(vehicle.vehicle.passengers ?? 0));
        passenger[bucketKey] = (passenger[bucketKey] ?? 0) + 1;
      });

      return {
        prices: priceList,
        priceByVehicle: priceMap,
        fuelTypeCounts: fuel as Readonly<Record<string, number>>,
        carTypeCounts: carType as Readonly<Record<string, number>>,
        companyCounts: company as Readonly<Record<string, number>>,
        driveTypeCounts: drive as Readonly<Record<string, number>>,
        passengerBucketCounts: passenger as Readonly<Record<string, number>>,
        transmissionCounts: transmission as Readonly<Record<string, number>>,
        cancellationTypeCounts: cancellation as Readonly<Record<string, number>>,
      };
    }, [vehicles]);

  const dynamicPriceMax = useMemo(() => computeDynamicCarPriceMax(prices), [prices]);

  const priceOf = useCallback((vehicle: CarVehicle): number => priceByVehicle.get(vehicle) ?? 0, [priceByVehicle]);

  const priceRange = useMemo<readonly [number, number]>(() => {
    if (!isPriceRangeCustomized) return [CAR_PRICE_RANGE.MIN, dynamicPriceMax];
    const [min, max] = filters.priceRange;
    return [Math.max(CAR_PRICE_RANGE.MIN, min), Math.min(max, dynamicPriceMax)];
  }, [isPriceRangeCustomized, filters.priceRange, dynamicPriceMax]);

  const supportedCarTypes = useMemo(() => Object.keys(carTypeCounts).sort(), [carTypeCounts]);
  const supportedCompanies = useMemo(() => Object.keys(companyCounts).sort(), [companyCounts]);
  const supportedTransmissions = useMemo(() => Object.keys(transmissionCounts).sort(), [transmissionCounts]);
  const supportedFuelTypes = useMemo(() => Object.keys(fuelTypeCounts).sort(), [fuelTypeCounts]);
  const supportedDriveTypes = useMemo(() => Object.keys(driveTypeCounts).sort(), [driveTypeCounts]);
  const supportedCancellationTypes = useMemo(() => Object.keys(cancellationTypeCounts).sort(), [cancellationTypeCounts]);

  const filteredVehicles = useMemo(() => {
    const normalizedSearch = carNameSearch.trim().toLowerCase();

    const [rangeStart, rangeEnd] = priceRange;
    const minPrice = Math.min(rangeStart, rangeEnd);
    const maxPrice = Math.max(rangeStart, rangeEnd);
    const isPriceFiltered = minPrice > CAR_PRICE_RANGE.MIN || maxPrice < dynamicPriceMax;

    const carTypeSet = new Set(filters.carTypes);
    const companySet = new Set(filters.companies);
    const transmissionSet = new Set(filters.transmissionTypes);
    const fuelTypeSet = new Set(filters.fuelTypes);
    const driveTypeSet = new Set(filters.driveTypes);
    const cancellationTypeSet = new Set(filters.cancellationTypes);
    const passengerBucketSet = new Set(filters.passengerBuckets);

    const matched = vehicles.filter(vehicle => {
      if (normalizedSearch && !`${vehicle.description} ${vehicle.vendor.name}`.toLowerCase().includes(normalizedSearch)) return false;
      if (isPriceFiltered) {
        const price = priceOf(vehicle);
        if (price < minPrice || price > maxPrice) return false;
      }
      if (carTypeSet.size && !carTypeSet.has(vehicle.vehicle.category)) return false;
      if (companySet.size && !companySet.has(vehicle.vendor.name)) return false;
      if (transmissionSet.size && !transmissionSet.has(vehicle.vehicle.transmissionType)) return false;
      if (fuelTypeSet.size && !fuelTypeSet.has(vehicle.vehicle.fuelType)) return false;
      if (driveTypeSet.size && !driveTypeSet.has(vehicle.vehicle.driveType)) return false;
      if (cancellationTypeSet.size && !cancellationTypeSet.has(cancellationTypeOf(vehicle))) return false;
      if (filters.airConditionedOnly && !vehicle.vehicle.airConditioned) return false;
      if (passengerBucketSet.size && !passengerBucketSet.has(bucketForPassengers(vehicle.vehicle.passengers ?? 0))) return false;
      return true;
    });

    return matched;
  }, [vehicles, carNameSearch, filters, priceRange, dynamicPriceMax, priceOf]);

  const updateFilters = useEventCallback((next: CarFilters) => setFilters(next));
  const updatePriceRange = useEventCallback((range: readonly [number, number]) => {
    setIsPriceRangeCustomized(true);
    setFilters(prev => ({ ...prev, priceRange: range }));
  });
  const resetFilters = useEventCallback(() => {
    setFilters(DEFAULT_CAR_FILTERS);
    setIsPriceRangeCustomized(false);
    setCarNameSearch('');
  });

  return {
    filters,
    priceRange,
    resetFilters,
    carNameSearch,
    carTypeCounts,
    companyCounts,
    updateFilters,
    fuelTypeCounts,
    driveTypeCounts,
    dynamicPriceMax,
    setCarNameSearch,
    filteredVehicles,
    updatePriceRange,
    supportedCarTypes,
    supportedCompanies,
    transmissionCounts,
    supportedFuelTypes,
    supportedDriveTypes,
    passengerBucketCounts,
    supportedTransmissions,
    cancellationTypeCounts,
    supportedCancellationTypes,
  };
};
